/**
 * ClickChutney Simple Components
 * No hooks, just simple click handlers
 */

import React from 'react';
import type { TrackingEvent } from './types';

export interface ClickTrackerProps {
  children: React.ReactNode;
  eventName?: string;
  properties?: TrackingEvent;
  onClick?: (event: React.MouseEvent) => void;
  className?: string;
  [key: string]: any;
}

export interface FormTrackerProps extends React.FormHTMLAttributes<HTMLFormElement> {
  children: React.ReactNode;
  formName: string;
  trackSubmit?: boolean;
  properties?: TrackingEvent;
  includeFormData?: boolean; // New: Option to include serialized form data
}

/**
 * Simple click tracker - no hooks
 */
export function ClickTracker({ 
  children, 
  eventName = 'click',
  properties = {},
  onClick,
  ...props 
}: ClickTrackerProps) {
  const handleClick = (event: React.MouseEvent) => {
    // Only track on client side
    if (typeof window !== 'undefined' && (window as any).cc) {
      const target = event.currentTarget;
      const enhancedProperties = {
        ...properties,
        element: {
          tag: target.tagName.toLowerCase(),
          text: target.textContent?.trim() || '',
          classes: target.className,
          id: target.id
        }
      };
      (window as any).cc('event', eventName, enhancedProperties);
    }
    if (onClick) onClick(event);
  };

  // If children is a single React element, clone and attach onClick to it
  if (React.Children.count(children) === 1 && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement, {
      ...props,
      onClick: handleClick
    });
  }

  // Otherwise, wrap in div
  return (
    <div {...props} onClick={handleClick}>
      {children}
    </div>
  );
}

/**
 * Simple form tracker - no hooks
 */
export function FormTracker({ 
  children, 
  formName,
  trackSubmit = true,
  properties = {},
  includeFormData = false,
  onSubmit,
  ...props 
}: FormTrackerProps) {
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    if (trackSubmit && typeof window !== 'undefined' && (window as any).cc) {
      const enhancedProperties: Record<string, any> = { ...properties, form: formName };

      if (includeFormData) {
        const formData = new FormData(event.currentTarget);
        const data: Record<string, any> = {};
        formData.forEach((value, key) => {
          if (data[key]) {
            if (!Array.isArray(data[key])) {
              data[key] = [data[key]];
            }
            data[key].push(value);
          } else {
            data[key] = value;
          }
        });
        enhancedProperties.formData = data;
      }

      (window as any).cc('event', 'form_submit', enhancedProperties);
    }
    if (onSubmit) onSubmit(event);
  };

  return (
    <form {...props} onSubmit={handleSubmit}>
      {children}
    </form>
  );
}